import React from 'react'
import styled, {keyframes} from 'styled-components'
import {themeGet, sx} from '@fuegokit/react'
import {AkCalendarIcon, AkChevronLeftIcon, AkChevronRightIcon} from '@fuegokit/fuegoicons-react'
import Datepicker from '../../../src/components/Datepicker'

const StyledDatepickerExample = () => {
  return (
    <StyledDatepicker>
      <Datepicker.Label>Date</Datepicker.Label>
      <StyledDatepickerGroup>
        <StyledDatepickerDateInput>
          {segment => <StyledDatepickerDateSegment segment={segment} />}
        </StyledDatepickerDateInput>
        <StyledDatepickerButton>
          <AkCalendarIcon />
        </StyledDatepickerButton>
      </StyledDatepickerGroup>
      <StyledDatepickerPopover>
        <Datepicker.Dialog>
          <StyledDatepickerCalendar>
            <header>
              <StyledDatepickerButton slot="previous" sx={{marginLeft: '1px'}}>
                <AkChevronLeftIcon />
              </StyledDatepickerButton>
              <StyledDatepickerHeading />
              <StyledDatepickerButton slot="next">
                <AkChevronRightIcon />
              </StyledDatepickerButton>
            </header>
            <Datepicker.CalendarGrid>{date => <StyledDatepickerCalendarCell date={date} />}</Datepicker.CalendarGrid>
          </StyledDatepickerCalendar>
        </Datepicker.Dialog>
      </StyledDatepickerPopover>
    </StyledDatepicker>
  )
}

export const StyledDatepicker = styled(Datepicker)`
  --text-color-invalid: ${themeGet('colors.text.danger')};

  [slot='description'] {
    font-size: 12px;
  }

  [slot='errorMessage'] {
    font-size: 12px;
    color: var(--text-color-invalid);
  }
  @media (forced-colors: active) {
    .react-aria-DatePicker {
      --text-color-invalid: LinkText;

      .react-aria-Button {
        forced-color-adjust: none;

        --focus-ring-color: Highlight;
        background: ButtonFace;
        color: ButtonText;
        border: 1px solid ButtonBorder;

        &[data-focus-visible] {
          border-color: Highlight;
          box-shadow: 0 0 0 1px Highlight;
        }
      }
    }
    .react-aria-DateInput {
      forced-color-adjust: none;

      --field-border: ButtonBorder;
      --field-background: Field;
      --text-color: FieldText;
      --text-color-placeholder: FieldText;
      --text-color-invalid: LinkText;
      --highlight-background: Highlight;
      --highlight-foreground: HighlightText;
      --highlight-background-invalid: LinkText;
    }
    .react-aria-Calendar {
      forced-color-adjust: none;

      --highlight-background: Highlight;
      --highlight-foreground: HighlightText;
      --button-border: ButtonBorder;
      --button-background: ButtonFace;
      --button-backgound-pressed: ButtonFace;
      --text-color: ButtonText;
      --text-color-disabled: GrayText;
      --text-color-invalid: LinkText;
      --unavailable-color: GrayText;
      --invalid-color: LinkText;
      --pressed-color: Canvas;
      --spectrum-alias-background-color-default: Canvas;
    }
  }
`
export const StyledDatepickerGroup = styled(Datepicker.Group)`
  display: flex;
  width: fit-content;
  align-items: center;
`
export const StyledDatepickerButton = styled(Datepicker.Button)`
  background: ${themeGet('colors.background.neutral.subtle.default')};
  color: ${themeGet('colors.icon.default')};
  border-radius: ${themeGet('radii.2')};
  border: none;
  appearance: none;
  margin-left: -1.929rem;
  width: 1.429rem;
  height: 1.429rem;
  padding: 0;
  vertical-align: middle;

  outline: none;
  box-sizing: content-box;
  border: 2px solid ${themeGet('colors.elevation.surface.default.[default]')};
  &:hover {
    background: ${themeGet('colors.background.neutral.subtle.hovered')};
    cursor: pointer;
  }
  &[data-focus-visible] {
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
  &[slot='next'] {
  }
  &[slot='previous'] {
  }
  ${sx}
`
export const StyledDatepickerDateInput = styled(Datepicker.DateInput)`
  --field-border: ${themeGet('colors.border.default')};
  --field-background: ${themeGet('colors.background.input.default')};
  --text-color: ${themeGet('colors.text.default')};
  --text-color-placeholder: ${themeGet('colors.text.subtlest')};
  --text-color-invalid: ${themeGet('colors.text.danger')};
  --highlight-background: ${themeGet('colors.border.focused')};
  --highlight-foreground: ${themeGet('colors.text.default')};
  --highlight-background-invalid: ${themeGet('colors.background.danger.default.[default]')};

  display: flex;

  font-size: ${themeGet('fontSizes.3')};
  min-width: 0;
  outline: none;

  line-height: ${(8 * 2.5) / 14};

  padding: 8px 6px;
  // @TODO: revisit with tokenized font-size
  height: ${((8 * 4.5) / 14).toFixed(2)}em;

  border: 1px solid var(--field-border);
  border-radius: ${themeGet('radii.1')};

  background: var(--field-background);
  width: 100%;
  white-space: nowrap;

  &:focus-within {
    border-color: var(--highlight-background);
    box-shadow: 0 0 0 1px var(--highlight-background);
  }
  min-width: 294px;
  @media (min-width: ${themeGet('breakpoints.2')}) {
    min-width: 354px;
  }
`
export const StyledDatepickerDateSegment = styled(Datepicker.DateSegment)`
  padding: 0 2px;
  font-variant-numeric: tabular-nums;
  text-align: end;
  color: var(--text-color);
  border-radius: ${themeGet('radii.1')};
  &[data-type='literal'] {
    padding: 0;
  }

  &[data-placeholder] {
    color: ${themeGet('colors.text.subtlest')};
    font-style: italic;
  }

  &:focus {
    color: ${themeGet('colors.text.default')};
    background: ${themeGet('colors.background.selected.default.[default]')};
    outline: none;
    border-radius: ${themeGet('radii.1')};
    caret-color: transparent;
  }

  &[aria-invalid] {
    color: var(--text-color-invalid);

    &:focus {
      background: ${themeGet('colors.background.danger.default.[default]')};
      color: ${themeGet('colors.text.danger')};
    }
  }
`
export const StyledDatepickerHeading = styled(Datepicker.Heading)`
  flex: 1;
  margin: 0;
  text-align: center;
  font-size: ${themeGet('fontSizes.2')};
  font-weight: ${themeGet('fontWeights.semibold')};
  // visually align the heading since we use negative margin on 'next' button
  margin-left: -1.839rem;
  user-select: none;
  svg {
    user-select: revert;
  }
`

export const StyledDatepickerCalendarCell = styled(Datepicker.CalendarCell)`
  /* width: ${themeGet('space.5')}; */
  padding: 0 ${themeGet('space.2')} 0 ${themeGet('space.2')};
  line-height: 2rem;
  text-align: center;
  border-radius: ${themeGet('radii.2')};
  cursor: default;
  outline: none;
  border: 2px solid ${themeGet('colors.elevation.surface.default.[default]')};

  &:hover {
    background: ${themeGet('colors.background.neutral.subtle.hovered')};
  }

  &[data-outside-month] {
    display: none;
  }

  &[data-pressed] {
    background: ${themeGet('colors.background.selected.default.pressed')};
  }

  &[data-focus-visible] {
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }

  &[data-selected] {
    background: ${themeGet('colors.background.selected.default.[default]')};
    color: ${themeGet('colors.text.selected')};
  }

  &[data-disabled] {
    color: ${themeGet('colors.text.disabled')};
  }

  &[data-unavailable] {
    text-decoration: line-through;
    color: ${themeGet('colors.text.danger')};
  }

  &[aria-invalid] {
    background: ${themeGet('colors.background.danger.default.[default]')};
    color: ${themeGet('colors.text.danger')};
  }
`

export const StyledDatepickerCalendar = styled(Datepicker.Calendar)`
  --highlight-background: ${themeGet('colors.background.selected.default.[default]')};
  --highlight-foreground: ${themeGet('colors.text.selected')};
  --button-background: ${themeGet('colors.background.neutral.subtle.default')};
  --button-background-pressed: ${themeGet('colors.background.neutral.subtle.pressed')};
  --button-border: ${themeGet('colors.border.default')};
  --text-color: ${themeGet('colors.text.default')};
  --text-color-disabled: ${themeGet('colors.text.disabled')};
  --text-color-invalid: ${themeGet('colors.text.danger')};
  --pressed-color: ${themeGet('colors.text.selected')};
  --unavailable-color: ${themeGet('colors.text.danger')};
  --invalid-color: ${themeGet('colors.text.danger')};

  width: fit-content;
  color: var(--text-color);

  & header {
    display: flex;
    align-items: center;
    margin: 0 ${themeGet('space.1')} 0.5rem ${themeGet('space.1')};

    /* ${StyledDatepickerButton} & {
      background: var(--button-background);
      background: red;
      border: 1px solid var(--button-border);
      color: var(--text-color);
      box-shadow: 0 1px 2px rgba(0 0 0 / 0.1);
      border-radius: 4px;
      appearance: none;
      vertical-align: middle;
      font-size: 1rem;
      width: 2rem;
      height: 2rem;
      padding: 0;
      text-align: center;
      margin: 0;
      outline: none;

      &[data-focus-visible] {
        border-color: var(--highlight-background);
        box-shadow: 0 0 0 1px var(--highlight-background);
      }

      &[data-pressed] {
        background: var(--button-background-pressed);
      }
    } */
  }

  [slot='errorMessage'] {
    font-size: 12px;
    color: var(--text-color-invalid);
  }
`
const slide = keyframes`
  from {
    transform: var(--origin);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
`
export const StyledDatepickerPopover = styled(Datepicker.Popover)`
  overflow: auto;
  border: 1px solid ${themeGet('colors.border.default')};
  /* box-shadow: 0 8px 20px rgba(0 0 0 / 0.1); */
  box-shadow: ${themeGet('shadows.elevation.shadow.overlay')};
  border-radius: ${themeGet('radii.3')};
  background: ${themeGet('colors.elevation.surface.default.[default]')};
  padding: ${themeGet('space.4')};

  &[data-placement='top'] {
    --origin: translateY(8px);
  }

  &[data-placement='bottom'] {
    --origin: translateY(-8px);
  }

  &[data-entering] {
    animation: ${slide} 150ms;
  }

  &[data-exiting] {
    animation: ${slide} 150ms reverse ease-in;
  }
`
export default StyledDatepickerExample
